/**
 * THE SHELL. One context bar, one content area, one scroll owner.
 *
 * 09 §5.4 T3: ONE scroll owner per screen. The shell is a fixed-height flex column — the bar never
 * scrolls, and the content area is the single scroll region. A grid that scrolls inside a page that
 * also scrolls is a defect (T4), so the shell gives the grid the only scrollport there is.
 *
 * The shell also carries the surface chrome: `data-surface` on the root switches the whole palette,
 * so a PLATFORM screen cannot be mistaken for a TENANT screen at a glance (09 §"two surfaces").
 */

import type { ReactNode } from 'react';
import { ContextBar } from './ContextBar';
import { useWorkContext } from './ContextProvider';
import { fixturesEnabled, FIXTURE_NOTICE_HE } from '../api/fixtures';

export interface AppShellProps {
  /** 09 §9.1 V4: a noun phrase in office Hebrew. If it cannot be named, it is not clear enough to ship. */
  readonly titleHe: string;
  readonly backToParent?: { readonly labelHe: string; readonly href: string };
  /** 09 §5.9 — provenance line, bottom-left of the content area, muted. */
  readonly provenanceHe?: string;
  readonly children: ReactNode;
}

export function AppShell({ titleHe, backToParent, provenanceHe, children }: AppShellProps): JSX.Element {
  const ctx = useWorkContext();

  return (
    <div className="app-shell" data-surface={ctx.surface}>
      {backToParent === undefined ? <ContextBar /> : <ContextBar backToParent={backToParent} />}

      {fixturesEnabled() ? (
        // The fixtures must never be mistakable for a company's real numbers.
        <div className="fixture-ribbon" role="status">
          {FIXTURE_NOTICE_HE}
        </div>
      ) : null}

      <main className="app-shell__content">
        <h1 className="screen-title">{titleHe}</h1>
        {children}
        {provenanceHe === undefined ? null : (
          <p className="provenance" dir="rtl">
            {provenanceHe}
          </p>
        )}
      </main>
    </div>
  );
}
